import type { Shape } from "../types";

/** Sommet et base d'un volume en mètres, au repos et au survol. */
export type ExtrusionProps = { height: number; base: number; hoverHeight: number; hoverBase: number };

/**
 * Sommet et base du volume, au repos et au survol (`hoverHeight`/`hoverElevation` quand ils sont
 * positifs, à défaut ceux du repos) — `null` si la forme ne s'élève dans aucun des deux états. Une forme qui ne s'élève qu'au
 * survol reste à plat au repos (hauteur 0). En aperçu du survol (`previewingHover`), le survol vaut
 * pour les deux.
 */
export function extrusionProps(
  shape: Pick<Shape, "height" | "elevation" | "hoverHeight" | "hoverElevation">,
  previewingHover = false,
): ExtrusionProps | null {
  const volume = (h: number | undefined, e: number | undefined) => {
    if (!h || h <= 0) return { top: 0, base: 0 };
    const base = e && e > 0 ? e : 0;
    return { top: base + h, base };
  };
  const normal = volume(shape.height, shape.elevation);
  const positive = (value: number | undefined) => (value && value > 0 ? value : undefined);
  const hover = volume(positive(shape.hoverHeight) ?? shape.height, positive(shape.hoverElevation) ?? shape.elevation);
  if (!normal.top && !hover.top) return null;
  const rest = previewingHover ? hover : normal;
  return { height: rest.top, base: rest.base, hoverHeight: hover.top, hoverBase: hover.base };
}

export const DEFAULT_VOLUME_OPACITY = 0.9;

/**
 * Opacité d'un volume en dixièmes, de 0 à 10 : MapLibre n'accepte qu'une opacité par couche de
 * volumes, chaque dixième a donc la sienne.
 */
export function volumeOpacityTenths(opacity: number | undefined): number {
  const value = opacity ?? DEFAULT_VOLUME_OPACITY;
  if (!Number.isFinite(value)) return Math.round(DEFAULT_VOLUME_OPACITY * 10);

  return Math.min(10, Math.max(0, Math.round(value * 10)));
}
